import { useApolloClient } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { PrivacyToggleProvider } from '@eluve/blocks';
import {
  Icon,
  Layout,
  Main,
  Sidebar,
  SidebarContent,
  SidebarContentSpacer,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarLogoHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuLinkButton,
  SidebarMenuNavButton,
  useSidebar,
} from '@eluve/components';
import { EluveGptTrigger } from '@eluve/feature-chat-with-patient-data';
import { MedplumProvider } from '@eluve/feature-patients';
import { useNamedLogger } from '@eluve/logger';
import { usePostHog } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import {
  FeatureFlaggedComponent,
  GlobalStatusWidget,
  TenantOrUserFeatureFlaggedComponent,
  UserFeatureFlaggedComponent,
  useTenant,
} from '@eluve/smart-blocks';
import { useAppointmentRecordingStatusStoreApi } from '@eluve/user-local-files';
import { SUPPORT_URL } from '@eluve/utils';

import { showIntercom, useIntercomStatus } from '../../../../IntercomProvider';
import { appConfig } from '../../../../config';
import { TenantUserSettings } from '../../TenantUserSettings';
import { UserDropdownMenu } from '../components/UserDropdownMenu';

import { ImpersonationExpiryMonitor } from './ImpersonationExpiryMonitor';
import { TranscriptionWidget } from './appointments/machines/transcription/components/TranscriptionWidget';
import { useTranscriptionMachineSelector } from './appointments/machines/transcription/hooks/useTranscriptionMachine';

const IntercomSupportButton = () => {
  const { overlayOpen, setOverlayOpen } = useSidebar();
  const { isIntercomReady } = useIntercomStatus();

  if (isIntercomReady) {
    return (
      <SidebarMenuButton
        hideIconWhenExpanded
        onClick={() => {
          showIntercom();
          if (overlayOpen) {
            setOverlayOpen(false);
          }
        }}
        tooltip="Support"
      >
        <Icon name="CircleHelp" size="auto" />
        <span>Support</span>
      </SidebarMenuButton>
    );
  }

  return (
    <SidebarMenuLinkButton
      hideIconWhenExpanded
      href={SUPPORT_URL}
      tooltip="Support"
    >
      <Icon name="CircleHelp" size="auto" />
      <span>Support</span>
    </SidebarMenuLinkButton>
  );
};

export const TenantLayout = () => {
  const client = useApolloClient();
  const logger = useNamedLogger('TenantLayout');
  const navigate = useNavigate();
  const postHog = usePostHog();
  const tenantId = useAssignedTenantIdFromParams();
  const tenant = useTenant(tenantId);
  const userId = useUserIdFromSession();

  const isIdleOrPaused = useTranscriptionMachineSelector(
    (state) => state.matches('Idle') || state.matches({ InSession: 'Paused' }),
  );
  const isRecording = useTranscriptionMachineSelector((state) =>
    state.matches({ InSession: 'Recording' }),
  );

  const appointmentRecordingStatusStore =
    useAppointmentRecordingStatusStoreApi();

  useEffect(
    function syncAppointmentRecordingStatus() {
      appointmentRecordingStatusStore.setState({
        isActivelyRecording: !isIdleOrPaused,
      });
    },
    [isIdleOrPaused, appointmentRecordingStatusStore],
  );

  const localStorageKey = `last-tenant-id-for-user-${userId}`;

  const lastTenantId = useMemo(
    () => localStorage.getItem(localStorageKey),
    [localStorageKey],
  );

  useEffect(
    function trackLastAccessedTenantId() {
      if (tenantId !== lastTenantId) {
        localStorage.setItem(localStorageKey, tenantId);
      }
    },
    [tenantId, lastTenantId, client, logger, localStorageKey],
  );

  useEffect(() => {
    if (!tenant) return;
    postHog.group('tenant', tenantId, {
      name: tenant.name,
      createdAt: tenant.createdAt,
      isRealTenant: tenant.isRealTenant,
    });
  }, [
    postHog,
    tenantId,
    tenant?.name,
    tenant?.createdAt,
    tenant?.isRealTenant,
  ]);

  const handleLogoClick = () => {
    navigate(`/organizations/${tenantId}/home`);
  };

  return (
    <Layout>
      <Sidebar variant="inset">
        <SidebarHeader>
          <SidebarLogoHeader onClick={handleLogoClick} />
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuNavButton
                    size="lg"
                    to={`/organizations/${tenantId}/home`}
                    tooltip="Overview"
                  >
                    <Icon name="Calendar" size="auto" />
                    <span>Overview</span>
                  </SidebarMenuNavButton>
                </SidebarMenuItem>
                <FeatureFlaggedComponent flag="APPOINTMENTS_VIEW">
                  <SidebarMenuItem>
                    <SidebarMenuNavButton
                      size="lg"
                      to={`/organizations/${tenantId}/appointments`}
                      tooltip="Appointments"
                    >
                      <Icon name="FileEdit" size="auto" />
                      <span>Appointments</span>
                    </SidebarMenuNavButton>
                  </SidebarMenuItem>
                </FeatureFlaggedComponent>
                <FeatureFlaggedComponent flag="PATIENTS_VIEW">
                  <SidebarMenuItem>
                    <SidebarMenuNavButton
                      size="lg"
                      to={`/organizations/${tenantId}/patients`}
                      tooltip="Patients"
                    >
                      <Icon name="User" size="auto" />
                      <span>Patients</span>
                    </SidebarMenuNavButton>
                  </SidebarMenuItem>
                </FeatureFlaggedComponent>
                <UserFeatureFlaggedComponent flag="CARE_PLANS">
                  <SidebarMenuItem>
                    <SidebarMenuNavButton
                      size="lg"
                      to={`/organizations/${tenantId}/care-library`}
                      tooltip="Care Library"
                    >
                      <Icon name="Layers" size="auto" />
                      <span>Care Library</span>
                    </SidebarMenuNavButton>
                  </SidebarMenuItem>
                </UserFeatureFlaggedComponent>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          <SidebarContentSpacer />
          <SidebarGroup>
            <SidebarGroupContent>
              <GlobalStatusWidget isRecording={isRecording} />
            </SidebarGroupContent>
          </SidebarGroup>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuNavButton
                    hideIconWhenExpanded
                    to={`/organizations/${tenantId}/settings`}
                    tooltip="Settings"
                  >
                    <Icon name="Settings" size="auto" />
                    <span>Settings</span>
                  </SidebarMenuNavButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <IntercomSupportButton />
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <UserDropdownMenu />
        </SidebarFooter>
      </Sidebar>
      <Main>
        <PrivacyToggleProvider>
          <TenantUserSettings>
            <ImpersonationExpiryMonitor />
            <MedplumProvider baseUrl={appConfig.VITE_MEDPLUM_BASE_URL}>
              <Outlet />
            </MedplumProvider>
          </TenantUserSettings>
        </PrivacyToggleProvider>
      </Main>
      <TranscriptionWidget />
      <TenantOrUserFeatureFlaggedComponent flag="ELUVE_GPT">
        <EluveGptTrigger
          tenantId={tenantId}
          baseUrl={appConfig.VITE_API_DOMAIN}
        />
      </TenantOrUserFeatureFlaggedComponent>
    </Layout>
  );
};
